import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import EssentialsStep from "./steps/EssentialsStep";
import TourCard from "./TourCard";
import Spotlight from "./Spotlight";
import { TOUR_FULL_CARDS } from "./tourConfig";
import { moduleLabel, modulesInGroup } from "../registry";
import { MODULE_DESCRIPTIONS } from "./moduleDescriptions";
import "./onboarding.css";

/* ══ ONBOARDING · COACH-MARK WIZARD ════════════════════════════
 *
 * The app renders fully beneath this component. Four UI
 * states, driven by step kind:
 *
 *   Welcome (step 0)        modal over dimmed workspace
 *   Overview (tour steps)   expanded sheet (never collapses)
 *   Action (tour steps)     expanded sheet → spotlight ring
 *                           + hint → user taps target →
 *                           hint hides while they work →
 *                           state grows → advance
 *   Done                    modal
 *
 * Dismissal: Close X on any sheet/modal fires finish() so
 * onboardingState.complete becomes true permanently. Restart
 * Onboarding in Settings is the only way to replay.
 * ═══════════════════════════════════════════════════════════ */

const ChevronRight = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3l4 4-4 4"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3l8 8M11 3l-8 8"/>
  </svg>
);

/* Per-step auto-advance contract.
   stateKey  — which array on `state` to observe
   verb{En,Ar}  — CTA verb */
/* STEP_ACTIONS / SPOTLIGHT_COPY used to drive the old "watch the state
   array and auto-advance when it grows" mode for the Users/Staff steps.
   The wizard no longer has any action cards — Phase B is a single info
   card that navigates to Directory and closes the wizard — so these are
   intentionally empty. Left here as an extension point for future
   spotlight-style cards. */
const STEP_ACTIONS = {};
const SPOTLIGHT_COPY = {};

export default function OnboardingWizard({
  state,
  dispatch,
  currentUser,
  platform,
  pkgAllows,
  platformModuleNames,
  lang,
  switchLang,
  navigateTo,
  currentView,
}) {
  const isRTL = lang === "ar";

  /* ── Package-filtered card list ─────────────────────────
     Overview cards stay if AT LEAST ONE module in their
     group is allowed. The Users/family-member action isn't
     a module so it's never package-gated. Other action
     cards filter on their own module key. */
  const fullCards = useMemo(
    () => TOUR_FULL_CARDS.filter(c => {
      if (c.kind === "overview") {
        return modulesInGroup(c.group).some(k => pkgAllows(k));
      }
      if (c.key === "Users") return true; /* workspace users always available */
      return pkgAllows(c.key);
    }),
    [pkgAllows]
  );

  const doneStep = fullCards.length + 1;

  /* ── Persisted wizard state ──────────────────────────── */
  const savedState = state.onboardingState || {};
  const [step, setStep] = useState(() => Math.min(savedState.currentStep || 0, doneStep));
  const [skipped, setSkipped] = useState(() => savedState.skipped || []);
  /* sheetMode: "expanded" (default) | "spotlight" (action step
     in progress). No pill/collapsed mode any more — the user
     closes via the X (which fires finish) rather than
     collapsing a companion. */
  const [sheetMode, setSheetMode] = useState("expanded");
  /* acting: user has tapped the spotlighted target, so hide
     hint + ring until they're done (state growth) or we
     auto-dismiss via the document-click listener below. */
  const [acting, setActing] = useState(false);
  const essentialsRef = useRef(null);

  const persist = useCallback((patch) => {
    const nowIso = new Date().toISOString();
    const merged = {
      ...(state.onboardingState || {}),
      startedAt: savedState.startedAt || nowIso,
      updatedAt: nowIso,
      ...patch,
    };
    dispatch({ type: "SET_CONFIG_KEY", payload: { key: "onboardingState", value: merged } });
  }, [dispatch, state.onboardingState, savedState.startedAt]);

  useEffect(() => {
    if (!savedState.startedAt) persist({ currentStep: step, skipped });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advance = (nextStep, nextSkipped = skipped) => {
    setStep(nextStep);
    setSkipped(nextSkipped);
    setSheetMode("expanded");
    setActing(false);
    persist({ currentStep: nextStep, skipped: nextSkipped });
  };
  const goBack = () => { if (step > 0) advance(step - 1); };
  const goNext = () => {
    if (step === 0 && essentialsRef.current) essentialsRef.current.save();
    advance(Math.min(step + 1, doneStep));
  };
  const goSkip = () => {
    /* If the current card is the terminal info card (Directory
       hand-off), skipping means "I'll do it myself later" — close
       the wizard directly, no Dashboard bounce. Otherwise advance
       to the next card and record the skip. */
    const card = step > 0 && step <= fullCards.length ? fullCards[step - 1] : null;
    if (card?.kind === "info") { finish(); return; }
    advance(step + 1, skipped.includes(step) ? skipped : [...skipped, step]);
  };
  const finish = useCallback(() => {
    const nowIso = new Date().toISOString();
    dispatch({
      type: "SET_CONFIG_KEY",
      payload: {
        key: "onboardingState",
        value: {
          ...(state.onboardingState || {}),
          complete: true,
          currentStep: doneStep,
          skipped,
          updatedAt: nowIso,
          completedAt: nowIso,
          manuallyRestarted: false,
        },
      },
    });
  }, [dispatch, state.onboardingState, doneStep, skipped]);

  /* ── Current card + auto-advance contract ──────────────── */
  const currentCard = step > 0 && step <= fullCards.length ? fullCards[step - 1] : null;
  const currentAction = currentCard ? STEP_ACTIONS[currentCard.key] : null;

  /* Snapshot on step entry; when the tracked array grows, bring the
     wizard back into expanded mode so the user confirms + advances on
     their own terms. Previously we auto-advanced after 900ms, which
     made the wizard feel like it vanished ("it disappeared after I
     added the user") — they want it persistent between actions. */
  const initialLenRef = useRef(null);
  const [actionDone, setActionDone] = useState(false);
  useEffect(() => {
    initialLenRef.current = currentAction
      ? (state[currentAction.stateKey] || []).length
      : null;
    setActionDone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  useEffect(() => {
    if (!currentAction || initialLenRef.current == null) return;
    const now = (state[currentAction.stateKey] || []).length;
    if (now > initialLenRef.current) {
      setActionDone(true);
      setSheetMode("expanded");
      setActing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state[currentAction?.stateKey]?.length]);

  /* ── Hide hint/ring when user taps the target ──────────
     Lets the real modal / form render without obstruction.
     Document-level capture listener so we catch the tap
     even on dynamically-mounted elements. */
  useEffect(() => {
    if (sheetMode !== "spotlight" || !currentCard?.spotlightTarget) return;
    const selector = `[data-onboarding-target="${currentCard.spotlightTarget}"]`;
    const handler = (e) => {
      if (e.target && e.target.closest && e.target.closest(selector)) {
        setActing(true);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [sheetMode, currentCard?.spotlightTarget]);

  const startSpotlight = (card) => {
    if (card.settingsTab) {
      try { sessionStorage.setItem("cmd_wss_initial_tab", card.settingsTab); } catch {}
    }
    if (card.view) navigateTo(card.view);
    setSheetMode("spotlight");
    setActing(false);
  };

  /* ── Welcome (step 0) ─────────────────────────────────── */
  if (step === 0) {
    return (
      <>
        <div className="ob-backdrop" />
        <div className={`ob-modal ${isRTL ? "rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div className="ob-eyebrow" style={{ margin: 0 }}>{isRTL ? "مرحباً" : "Welcome"}</div>
            <div className="ob-lang">
              <button className={`ob-lang-btn ${lang === "en" ? "active" : ""}`} onClick={() => switchLang("en")}>EN</button>
              <button className={`ob-lang-btn ${lang === "ar" ? "active" : ""}`} onClick={() => switchLang("ar")}>ع</button>
            </div>
          </div>
          <h2 className="ob-title">
            {isRTL ? `أهلاً، ${currentUser?.name || ""}` : `Hello, ${currentUser?.name || "there"}`}
          </h2>
          <p className="ob-dek">
            {isRTL
              ? "جولة قصيرة لتهيئة الأساسيات ثم لمحة عن كل قسم من أقسام التطبيق."
              : "A short tour to set the essentials, then a walk through the app's sections."}
          </p>
          <EssentialsStep
            ref={essentialsRef}
            state={state}
            dispatch={dispatch}
            currentUser={currentUser}
            platform={platform}
            lang={lang}
            isRTL={isRTL}
          />
          <div className="ob-actions" style={{ marginTop: 14 }}>
            <button className="ob-primary" onClick={goNext}>
              <span>{isRTL ? "ابدأ الجولة" : "Begin the tour"}</span>
              <ChevronRight />
            </button>
          </div>
          <div className="ob-foot-row">
            <button className="ob-ghost" onClick={finish}>
              {isRTL ? "تخطي الجولة" : "Skip the tour"}
            </button>
            <div className="ob-sheet-step">
              {isRTL ? `الخطوة 01 / ${String(doneStep + 1).padStart(2, "0")}` : `Step 01 / ${String(doneStep + 1).padStart(2, "0")}`}
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ── Done (final) ─────────────────────────────────────── */
  if (step === doneStep) {
    return (
      <>
        <div className="ob-backdrop" />
        <div className={`ob-modal ${isRTL ? "rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
          <div className="ob-eyebrow">{isRTL ? "اكتمل" : "All set"}</div>
          <h2 className="ob-title">{isRTL ? "أنت جاهز للبدء" : "You're ready"}</h2>
          <p className="ob-dek">
            {isRTL
              ? "يمكن إعادة تشغيل الجولة في أي وقت من الإعدادات."
              : "Restart this tour any time from Settings."}
          </p>
          <div className="ob-actions">
            <button className="ob-primary" onClick={() => { finish(); navigateTo("Dashboard"); }}>
              <span>{isRTL ? "الانتقال إلى لوحة التحكم" : "Go to Dashboard"}</span>
              <ChevronRight />
            </button>
          </div>
        </div>
      </>
    );
  }

  /* ── Spotlight mode — ring + hint, unless user is acting ── */
  if (sheetMode === "spotlight" && currentCard?.spotlightTarget) {
    if (acting) {
      /* User tapped the target — render nothing so the real
         modal / form is unobstructed. Auto-advance effect
         will resurface the wizard on the next step. */
      return null;
    }
    const copy =
      SPOTLIGHT_COPY[currentCard.key]?.[isRTL ? "ar" : "en"] || {
        title: isRTL ? currentCard.titleAr : currentCard.title,
        body: isRTL ? currentCard.introAr : currentCard.intro,
      };
    return (
      <>
        <Spotlight targetAttr={currentCard.spotlightTarget} />
        <div className={`ob-sp-hint ${isRTL ? "rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
          <h3 className="ob-sp-hint-title">{copy.title}</h3>
          <p className="ob-sp-hint-body">{copy.body}</p>
          <div className="ob-sp-hint-actions">
            <button className="ob-ghost" onClick={() => setSheetMode("expanded")}>
              {isRTL ? "عودة" : "Back"}
            </button>
            <button className="ob-primary" onClick={() => advance(step + 1)}>
              <span>{isRTL ? "تم" : "I've done it"}</span>
              <ChevronRight />
            </button>
          </div>
        </div>
      </>
    );
  }

  /* ── Expanded sheet (overview + action cards) ─────────── */
  let body;
  if (currentCard) {
    if (currentCard.kind === "overview") {
      /* Build list of modules in this group with name + desc */
      const mods = modulesInGroup(currentCard.group).filter(k => pkgAllows(k));
      const items = mods.map(k => ({
        name: moduleLabel(k, { pmn: platformModuleNames, isRTL }),
        desc: MODULE_DESCRIPTIONS[k]?.[isRTL ? "ar" : "en"] || "",
      }));
      const synth = { ...currentCard, modules: items };
      body = (
        <TourCard
          card={synth}
          isRTL={isRTL}
          moduleLabel={isRTL ? currentCard.titleAr : currentCard.title}
        />
      );
    } else {
      const fallbackLabel = currentCard.key === "Users" ? "Directory" : currentCard.key;
      const label = moduleLabel(fallbackLabel, { pmn: platformModuleNames, isRTL }) || fallbackLabel;
      body = <TourCard card={currentCard} isRTL={isRTL} moduleLabel={label} />;
    }
  }

  /* Primary CTA behaviour:
     - Info card (last card, Directory hand-off) → navigate to view and
       close the wizard. No "report back" checkpoint, no dashboard
       bounce — the user lands on Directory and the coach is done.
     - Action card, action completed → Continue (advances to next step)
     - Action card + spotlightTarget → start spotlight
     - User already viewing the target → Next (avoid re-nav)
     - Read-only card → Open module
     - Otherwise → Next */
  let primaryLabel = isRTL ? "التالي" : "Next";
  let onPrimary = goNext;
  if (currentCard && currentCard.kind === "info" && currentCard.view) {
    primaryLabel = isRTL ? currentCard.ctaAr : currentCard.cta;
    onPrimary = () => {
      if (currentCard.settingsTab) {
        try { sessionStorage.setItem("cmd_wss_initial_tab", currentCard.settingsTab); } catch {}
      }
      navigateTo(currentCard.view);
      finish();
    };
  } else if (currentCard && currentAction && actionDone) {
    primaryLabel = isRTL ? "متابعة" : "Continue";
    onPrimary = goNext;
  } else if (currentCard && currentAction && currentCard.spotlightTarget) {
    const alreadyThere = currentView === currentCard.view;
    primaryLabel = alreadyThere
      ? (isRTL ? "التالي" : "Next")
      : (isRTL ? currentAction.verbAr : currentAction.verbEn);
    onPrimary = alreadyThere ? goNext : () => startSpotlight(currentCard);
  } else if (currentCard && !currentCard.spotlight && currentCard.view) {
    const alreadyThere = currentView === currentCard.view;
    primaryLabel = alreadyThere
      ? (isRTL ? "التالي" : "Next")
      : (isRTL ? currentCard.ctaAr : currentCard.cta);
    onPrimary = alreadyThere
      ? goNext
      : () => { persist({ currentStep: step }); navigateTo(currentCard.view); };
  }

  const progress = Math.round(((step + 1) / (doneStep + 1)) * 100);

  return (
    <div className={`ob-sheet ${isRTL ? "rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <div className="ob-sheet-head">
        <span className="ob-sheet-step">
          {isRTL
            ? `الخطوة ${String(step + 1).padStart(2, "0")} / ${String(doneStep + 1).padStart(2, "0")}`
            : `Step ${String(step + 1).padStart(2, "0")} / ${String(doneStep + 1).padStart(2, "0")}`}
        </span>
        <button
          className="ob-sheet-close"
          aria-label={isRTL ? "إنهاء الجولة" : "Dismiss tour"}
          title={isRTL ? "إنهاء الجولة" : "Dismiss tour"}
          onClick={finish}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="ob-progress-track">
        <div className="ob-progress-fill" style={{ width: progress + "%" }} />
      </div>
      {body}
      {currentAction && actionDone && (
        <div className="ob-action-done" role="status">
          <span className="ob-action-done-check" aria-hidden="true">✓</span>
          <span>
            {currentCard.key === "Users"
              ? (isRTL ? "تمت إضافة المستخدم. تابع لخطوة إضافة الموظف." : "User added. Continue to add a staff member.")
              : (isRTL ? "تمت إضافة الموظف." : "Staff member added.")}
          </span>
        </div>
      )}
      <div className="ob-actions">
        <button className="ob-primary" onClick={onPrimary}>
          <span>{primaryLabel}</span>
          <ChevronRight />
        </button>
      </div>
      <div className="ob-foot-row">
        <button className="ob-ghost" onClick={goBack} disabled={step === 0}>
          {isRTL ? "رجوع" : "Back"}
        </button>
        <button className="ob-ghost" onClick={goSkip}>
          {isRTL ? "تخطي" : "Skip"}
        </button>
      </div>
    </div>
  );
}
